import * as React from 'react'
import {Box, Chip, Typography, ButtonBase} from '@mui/material'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, INFO, PURPLE, MUTED, withOpacity} from '../../../../styles/foundation/colors'
import {kg as fmtKg, date as fmtDate} from '../format'
import DwsPlate from './DwsPlate'

/**
 * THE HONESTY GATE, in one place.
 *
 * A record only has a real picture if a file was picked in THIS session —
 * that is the only way a blob url ever exists. Every courier record and
 * every seeded merchant record returns null here and renders as a DwsPlate
 * instead. Never widen this to "source === 'upload'": the frozen dataset's
 * 235 upload records all carry `url: null` on every image and on their
 * video, and treating them as pictures would render broken image icons
 * captioned as photographs that do not exist.
 */
export function recordImageUrl(record) {
  if (!record || record.source !== 'upload') return null
  const img = (record.images || []).find((i) => i && i.url)
  return img ? img.url : null
}

/**
 * The video mirror of recordImageUrl(), above — same gate, same reasoning.
 *
 * This exists because the Honesty Rule table cuts across BOTH media kinds:
 * a merchant can upload a packing video with no stills at all (row 'vid' of
 * DWS_STEPS calls video the single strongest evidence, "settles 97%" on its
 * own), and that record is exactly as real as an image-only upload. Gating
 * "does this record have a real file" on recordImageUrl() alone would tell
 * the opposite lie the whole module exists to prevent: captioning a video a
 * merchant just uploaded in this session as "not part of this dataset."
 */
export function recordVideoUrl(record) {
  if (!record || record.source !== 'upload') return null
  return record.video && record.video.url ? record.video.url : null
}

const chipSx = (color) => ({
  bgcolor: withOpacity(color, 0.12), color,
  fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], height: 22,
})

// PURPLE is the module's stand-in for the mockup's 'ai' tone, which has no
// MUI palette equivalent (map §3.8). disputes-v2/DisputeGrid.js already maps
// ai -> PURPLE; matched here so the two modules agree.
export default function DwsThumb({record, onOpen}) {
  const imgUrl = recordImageUrl(record)
  const videoUrl = imgUrl ? null : recordVideoUrl(record)
  const isMerchant = record.source === 'upload'

  return (
    <Box sx={{display: 'grid', gap: 1}}>
      <ButtonBase
        onClick={() => onOpen(record)}
        aria-label={`Open DWS record for AWB ${record.awb}, ${fmtDate(record.date)}`}
        sx={{
          p: 0, display: 'block', width: '100%', overflow: 'hidden',
          border: `1px solid ${BORDER}`, borderRadius: '8px', bgcolor: NAVY,
        }}
      >
        {imgUrl ? (
          <Box
            component='img'
            src={imgUrl}
            alt={`Merchant evidence you uploaded for AWB ${record.awb}`}
            sx={{display: 'block', width: '100%', aspectRatio: '220 / 140', objectFit: 'cover'}}
          />
        ) : videoUrl ? (
          <Box
            component='video'
            src={videoUrl}
            muted
            playsInline
            preload='metadata'
            aria-label={`Merchant evidence video you uploaded for AWB ${record.awb}`}
            sx={{display: 'block', width: '100%', aspectRatio: '220 / 140', objectFit: 'cover'}}
          />
        ) : (
          <DwsPlate record={record} />
        )}
      </ButtonBase>

      <Box sx={{display: 'grid', gap: 0.25}}>
        <Typography sx={{fontFamily: 'monospace', fontSize: SIZE['2xs'], color: NAVY}}>
          {record.awb}
        </Typography>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
          {isMerchant ? 'Merchant evidence' : 'Sorter image'} · {record.courier} · {fmtDate(record.date)}
        </Typography>
        <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5}}>
          <Chip size='small' label={`${fmtKg(record.read)} read`} sx={chipSx(INFO)} />
          {isMerchant
            ? <Chip size='small' label='Yours' sx={chipSx(PURPLE)} />
            : <Chip size='small' label={`${fmtKg(record.charged)} charged`} sx={chipSx(MUTED)} />}
        </Box>
      </Box>
    </Box>
  )
}
